import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@eduviaos/ui-kit";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/**
 * Gradient welcome hero with a 3D mascot. `variant` picks the role-aware
 * gradient (defined in index.css); `mascotShape` chooses a full-bleed
 * cutout (student) or a soft circular frame (parent).
 */
export function HeroCard({
  variant,
  title,
  subtitle,
  mascotSrc,
  mascotAlt,
  mascotShape = "full",
  children,
}: {
  variant: "student" | "parent";
  title: React.ReactNode;
  subtitle: React.ReactNode;
  mascotSrc: string;
  mascotAlt: string;
  mascotShape?: "full" | "circle";
  children?: React.ReactNode;
}) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: EASE_OUT }}
      className={cn(
        "hero-orbs relative overflow-hidden rounded-[1.75rem] px-5 pb-5 pt-6 text-white shadow-[var(--shadow-float)]",
        variant === "student" ? "hero-student" : "hero-parent",
      )}
    >
      <div className="relative z-10 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-[1.6rem] font-semibold leading-tight drop-shadow-sm">
            {title}
          </h1>
          <p className="mt-1 text-sm font-medium text-white/85">{subtitle}</p>
          {children && <div className="mt-3">{children}</div>}
        </div>

        {mascotShape === "circle" ? (
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE_OUT, delay: 0.12 }}
            className="grid size-24 shrink-0 place-items-center overflow-hidden rounded-full bg-white/20 ring-4 ring-white/30 backdrop-blur-sm"
          >
            <img src={mascotSrc} alt={mascotAlt} className="size-24 object-cover" loading="eager" />
          </motion.div>
        ) : (
          <motion.img
            src={mascotSrc}
            alt={mascotAlt}
            loading="eager"
            initial={reduce ? false : { opacity: 0, x: 18, y: 8 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.55, ease: EASE_OUT, delay: 0.12 }}
            className="pointer-events-none -mb-5 -mr-2 -mt-8 h-40 w-32 shrink-0 self-end object-contain drop-shadow-xl"
          />
        )}
      </div>
    </motion.div>
  );
}
